<template>
  <div id="knowledge-base">
    <v-row>
      <v-col cols="12" md="3" lg="3" order="1" order-md="2" class="knowledge-base-summary-overlay">
        <v-card class="knowledge-base-summary">
          <div class="kb-questions py-1">
            <div
              v-for="subTitle in summary"
              :key="subTitle.title"
              class="kb-question d-flex align-start text-sm px-5 my-4 cursor-pointer"
            >
              <v-icon size="14" class="me-2 mt-1" color="secondary">
                {{ icons.mdiCheckboxBlankCircleOutline }}
              </v-icon>
              <span class="text--secondary" @click="scrollToElement(subTitle.link)">{{ subTitle.title }}</span>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="9" lg="9" order="1" order-md="2">
        <v-card>
          <v-card-text id="init">
            <h2>Visa OI (Order Insight) Lookups</h2>
          </v-card-text>
          <v-card-text>
            Visa Order Insight (OI) is Visa’s global service that gives issuers and cardholders real-time access to purchase details directly from merchants.
            When a cardholder questions a transaction, Visa sends a lookup request to Merchanto, which then forwards it to your system via the Prevent API.
          </v-card-text>
          <v-card-text>
            By responding with transaction details, merchants can:
            <ul>
              <li>
                <b>Reduce billing confusion</b> by providing clear purchase details.
              </li>
              <li>
                <b>Prevent friendly fraud</b> by resolving cardholder doubts before they become disputes.
              </li>
              <li>
                <b>Qualify for Compelling Evidence (CE)</b>, which can shift liability for certain fraud disputes back to issuers.
              </li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-text id="types">
            <h2>Types of Visa OI Lookups (Orders)</h2>
          </v-card-text>
          <v-card-text>
            Visa lookup requests come in 3 types. All are delivered to the same endpoint; you can distinguish them by the source field.
            <br><br>
            <v-simple-table>
              <template #default>
                <thead>
                <tr>
                  <th class="text-left">Source</th>
                  <th class="text-left">Name</th>
                  <th class="text-left">When It Is Used</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td><b>OI</b></td>
                  <td>Order Insight</td>
                  <td>Standard issuer/cardholder inquiry, often via call center.</td>
                </tr>
                <tr>
                  <td><b>OID</b></td>
                  <td>Order Insight Digital</td>
                  <td>Issuer digital channel inquiry (e.g., mobile app, online banking).</td>
                </tr>
                <tr>
                  <td><b>CE</b></td>
                  <td>Compelling Evidence</td>
                  <td><b>Follow-up</b> lookup for historical transactions. Sent only if the OI/OID response meets Visa’s CE eligibility rules.</td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
            <br>
            <b>CE is dependent on OI/OID</b>. You will only receive a CE lookup if you first responded to an OI/OID with the required fields and identifiers.
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="endpoints">1. Endpoint Setup</v-card-title>
          <v-card-text>
            Merchants integrating with Visa Order Insight must provide an endpoint to receive lookup requests.
            <ul>
              <li><b>Example:</b> <code>POST /api/prevent/visa/orders</code></li>
            </ul>
            This path is only an example. You may define your own endpoint, provided that:
            <ul>
              <li>It accepts <b>POST</b> requests.</li>
              <li>It is secured with <b>HTTPS</b>.</li>
              <li>It does not use query parameters.</li>
              <li>It requires <b>Basic Authentication</b>.</li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="general-req">2. General requirements</v-card-title>
          <v-card-text>
            Merchanto forwards Visa lookup requests (OI, OID, CE) to your endpoint.
            <br>
            Your system must:
            <ul>
              <li>Validate <b>Basic Auth</b> credentials.</li>
              <li>Respond in <b>JSON</b> with header <code>Content-Type: application/json</code>.</li>
              <li>Return a valid response within <b>1000 ms</b>.</li>
            </ul>
            Examples and schema definitions are available in Swagger, where you can also validate your responses.
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="general-workflow">3. Workflow</v-card-title>
          <v-card-text>
           <ol>
             <li>A cardholder initiates an inquiry (mobile app, web banking, or call center).</li>
             <li>Visa generates a lookup request (<code>source = OI</code> or <code>OID</code>) and sends it to Merchanto.</li>
             <li>Merchanto forwards the request to your provided endpoint.</li>
             <li>Your system matches the request to internal order data and responds with the required details.</li>
             <li>Merchanto validates and stores the response, then sends it back to Visa.</li>
             <li>If your OI/OID response meets Visa’s <b>Compelling Evidence (CE)</b> eligibility rules and the issuer pursues a dispute, Visa may generate a <b>CE lookup</b> (<code>source = CE</code>). This request asks for historical transaction evidence for the same cardholder.</li>
           </ol>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="transaction-matching">4. Transaction Matching</v-card-title>
          <v-card-text>
            Accurate transaction matching is critical for maximizing deflection success.
          </v-card-text>
          <v-card-title class="subtitle-1" id="matching-fields">4.1. Matching Fields</v-card-title>
          <v-card-text>
            <v-simple-table>
              <template #default>
                <thead>
                <tr>
                  <th class="text-left">Field Name</th>
                  <th class="text-left">Description</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td><b>Acquirer Reference Number (ARN)</b></td>
                  <td>Acquirer Reference Number of the transaction, when available. <code>arn</code></td>
                </tr>
                <tr>
                  <td><b>Transaction Date</b></td>
                  <td>Date, time, and time zone of when the transaction was authorized in ISO 8601 UTC format. <code>transactionDate</code></td>
                </tr>
                <tr>
                  <td><b>Auth Code</b></td>
                  <td>Issuer authorization code for the Visa Sale/Authorization. <code>authCode</code></td>
                </tr>
                <tr>
                  <td><b>Transaction Amount and Currency</b></td>
                  <td>Total transaction amount and the currency it was authorized in. <code>transactionAmount.amount</code> + <code>transactionAmount.currency</code></td>
                </tr>
                <tr>
                  <td><b>Transaction ID</b></td>
                  <td>Unique Visa transaction identifier. <code>transactionId</code></td>
                </tr>
                <tr>
                  <td><b>Card BIN</b></td>
                  <td>First 6 digits of the card used in the transaction. <code>cardBin</code></td>
                </tr>
                <tr>
                  <td><b>Card Last4</b></td>
                  <td>Last 4 digits of the card used in the transaction. <code>cardLast4</code></td>
                </tr>
                <tr>
                  <td><b>Purchase Identifier</b></td>
                  <td>Order or invoice reference supplied by the merchant. <code>purchaseIdentifier</code></td>
                </tr>
                <tr>
                  <td><b>Payment Descriptor</b></td>
                  <td>Merchant statement/dynamic descriptor shown to the cardholder (e.g. "ONLINE*MERCHANT"). <code>paymentDescriptor</code></td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
          <v-card-title class="subtitle-1" id="how-to-implement-matching">4.2. How to implement transaction matching?</v-card-title>
          <v-card-text>
            Not every lookup request includes the full set of identifiers, and some fields (e.g. <b>ARN</b>, <b>Card BIN</b>) may be missing depending on the acquirer.
            To maximise the match rate, apply a <b>priority-based cascade</b>: try the strongest identifier first, and fall back to weaker combinations only if the previous step returned no result.
            <br><br>
            When a step yields <b>exactly one</b> match, return the transaction details with <code>HTTP 200</code>.
            If a step returns multiple candidates, continue to the next (more specific) condition to disambiguate.
            If none of the conditions produce a single match, or the cascade cannot narrow the result down to one transaction, return <code>HTTP 404</code> (“Not Found”).
          </v-card-text>
          <v-card-title class="subtitle-1" id="recommended-lookup-strategy">4.3. Recommended Lookup Strategy</v-card-title>
          <v-card-text>
            The flowchart and step-by-step grid below show a reference cascade you can adapt: try the strongest identifier first, fall back to weaker combinations only if the previous step returned no single match.
            <br/><br/>
            <div class="text-center my-4">
              <img
                :src="matchingGuidance"
                alt="Recommended Lookup Strategy — flowchart"
                style="max-width: 100%; height: auto;"
              />
            </div>
            <br>
            <b>Amount match:</b> the search amount must equal the stored transaction amount.
            If the search currency and the stored transaction currency differ, first convert the search amount into the transaction's currency and then compare; if the currencies are the same, compare the amounts directly.
            <br><br>
            <b>Guidelines:</b>
            <ul>
              <li>Apply a <b>±3 days tolerance</b> on <code>transactionDate</code> at every step to absorb timezone and settlement differences.</li>
              <li>Prefer <b>authorization records</b> over settled records — Visa looks up by authorization identifiers.</li>
              <li>Stop at the first step that yields <b>exactly one</b> match — do not continue evaluating weaker conditions.</li>
            </ul>
            <br>
            <b>Note:</b> some acquirers don't have some fields, e.g. <b>Stripe</b> doesn't have <b>ARN</b> and <b>Card BIN</b>.
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="request-response">5. Request & Response</v-card-title>
          <v-card-title class="subtitle-1" id="request-example">5.1. Request Example</v-card-title>
          <v-card-text>
            Merchanto will send Visa OI lookups to your endpoint:
            <br>
            <br>
            <pre class="output"><code>
            {
              "insightId": "9a8e7e02-97de-4eb4-a95b-508433d58baf",
              "clientId": 9978,
              "source": "OI",
              "cardBin": "411111",
              "cardLast4": "1234",
              "paymentType": "VISA",
              "token": "411111xxxxxx1111",
              "transactionDate": "2023-10-25T09:17:28Z",
              "transactionAmount": {
                "amount": 100.25,
                "currency": "USD"
              },
              "arn": "24692162324102146309799",
              "authCode": "368525",
              "acquirerBin": "469216",
              "cardAcceptorId": "DP210000856448",
              "purchaseIdentifier": "ONLINE05549686048",
              "transactionId": "4411111111111",
              "transactionType": "SALE",
              "paymentDescriptor": "ONLINE*MERCHANT",
              "paymentDescriptorContact": "3236555789",
              "mcc": "4899",
              "posEntryModeCode": "10",
              "eci": "7",
              "transactionRequestId": "6c865c86-e080-42c7-8ad3-cf7f676a4569"
            }
            </code></pre>
            <br>
            <b>Field Reference</b>: see <a class="cursor-pointer" @click="scrollToElement('visa-oi-lookup-request-fields')">9.1. Visa Lookup Request Fields</a> for full descriptions (field, type, availability, description).
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="response-example"><span class="subtitle-1">5.2. Response Example</span></v-card-title>
          <v-card-text>
            The following example shows the minimum required fields that must be included in your response for Visa Order Insight.
            <br>
            Keep in mind:
            <ul>
              <li>Providing <b>only the minimum fields</b> may technically qualify your response, but <b>the more data you can return, the higher your chances are of preventing a dispute</b>.</li>
              <li>Always provide <b>only relevant data</b>. For example, do not include shipping details if you do not sell physical goods.</li>
              <li>
                To participate in <b>Visa Compelling Evidence (CE)</b>, you must include at least <b>2 out of 4 persistent identifiers</b>:
                <ul>
                  <li><code>customerInformation.accountId</code></li>
                  <li><code>device.deviceId</code></li>
                  <li><code>device.ipAddress</code></li>
                  <li><code>device.deviceFingerprint</code></li>
                </ul>
              </li>
            </ul>
          </v-card-text>
          <v-card-text>
            <b>Minimum Response Example:</b>
            <pre class="output"><code>
           {
            "receipt": {
              "productsPurchasedList": [
                {
                  "productDescription": "Wireless Bluetooth Headphones"
                }
              ]
            },
            "merchantInformation": {
              "merchantName": "TechStore Inc.",
              "merchantUrl": "https://www.techstore.com",
              "merchantContactPhone": "+18005551234",
              "storeDetails": {
                "storeName": "TechStore Online"
              }
            },
            "customerInformation": {
              "accountId": "john_doe_123"
            },
            "device": {
              "ipAddress": "203.0.113.42"
            }
          }
            </code></pre>
            <br>
            <b>Field Reference</b>: see <a @click="scrollToElement('visa-oi-lookup-response-fields')">9.2 Visa Lookup Response Fields</a> for full descriptions (field, type, description).
            <br>
            Note: This example includes the <b>core mandatory fields</b> and two identifiers (<code>accountId</code> + <code>ipAddress</code>), making it <b>eligible for CE</b>.
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="http-response-code"><span class="subtitle-1">5.3. HTTP Response Codes</span></v-card-title>
          <v-card-text>
            Your API must respond with the following status codes when handling Visa Order Insight (OI, OID, CE) requests:
            <br><br>
            <v-simple-table>
              <template #default>
                <thead>
                <tr>
                  <th class="text-left">Status Code</th>
                  <th class="text-left">Meaning</th>
                  <th class="text-left">When to Use</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td><b>200 OK</b></td>
                  <td>Success</td>
                  <td>Your system found the transaction and returned a valid JSON response within the allowed time.</td>
                </tr>
                <tr>
                  <td><b>400 Bad Request</b></td>
                  <td>Invalid Request</td>
                  <td>The request was understood, but required fields are missing or invalid in your response payload.</td>
                </tr>
                <tr>
                  <td><b>401 Unauthorized</b></td>
                  <td>Authentication Failed</td>
                  <td>The Basic Auth credentials provided in the request were missing or incorrect.</td>
                </tr>
                <tr>
                  <td><b>404 Not Found</b></td>
                  <td>No Transaction Match</td>
                  <td>Your system could not find a matching transaction, or matching logic resulted in multiple possible matches.</td>
                </tr>
                <tr>
                  <td><b>500 Internal Server Error</b></td>
                  <td>Server Failure</td>
                  <td>Your system encountered an unexpected error and could not process the request.</td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
            <br>
            <b>Note:</b>
            <ul>
              <li>Always prefer returning <b>404</b> when no clear match is found, rather than sending incomplete or invalid data.</li>
              <li>Merchanto Platform will validate the payload. Invalid or late responses are not forwarded to Visa and will typically result in a dispute.</li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="lookup-status-mapping"><span class="subtitle-1">5.4. Lookup Status Mapping in Merchanto Platform</span></v-card-title>
          <v-card-text>
            The table below shows how your system’s HTTP response is translated into a lookup status inside the Merchanto Prevent platform. This helps you monitor and reconcile lookups in our dashboard.
            <br><br>
            <v-simple-table>
              <template #default>
                <thead>
                <tr>
                  <th class="text-left">Merchant Response (HTTP)</th>
                  <th class="text-left">Lookup Status in Merchanto</th>
                  <th class="text-left">Description</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td><b>200 OK</b>(valid JSON)</td>
                  <td><b>Accepted / Timeout</b></td>
                  <td>The lookup was successfully matched and returned to Visa / did not respond in time.</td>
                </tr>
                <tr>
                  <td><b>200 OK</b>(invalid JSON or missing required fields)</td>
                  <td><b>Invalid</b></td>
                  <td>Response received, but rejected due to schema or content errors.</td>
                </tr>
                <tr>
                  <td><b>404 Not Found</b></td>
                  <td><b>Not Found</b></td>
                  <td>No matching transaction in merchant system.</td>
                </tr>
                <tr>
                  <td><b>400 Bad Request / 401 Unauthorized / 500 Internal Server Error</b></td>
                  <td><b>Failed</b></td>
                  <td>Lookup was not processed.</td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
            <br>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="compelling-evidence-ce">6. Compelling Evidence (CE)</v-card-title>
          <v-card-title id="ce-overview"><span class="subtitle-1">6.1. Overview</span></v-card-title>
          <v-card-text><b>Compelling Evidence (CE)</b> is a special Visa Order Insight lookup (<code>source = CE</code>) used to help merchants defend against card-not-present fraud disputes (reason code 10.4). CE lookups are not always sent — Visa triggers them only if strict conditions are satisfied.</v-card-text>
          <v-divider></v-divider>
          <v-card-title id="ce-lookups-conditions"><span class="subtitle-1">6.2. Conditions for CE Lookups</span></v-card-title>
          <v-card-text>
            Visa will send CE lookups <b>only if all of the following are true</b>:
            <ol>
              <li>Your <b>OI/OID response</b> contained the minimum required CE fields.</li>
              <li>There are at least <b>two historical transactions</b> for the same PAN, settled <b>more than 120 days and less than 365 days</b> before the dispute date.</li>
              <li>The historical transactions have <b>no prior fraud reports</b> (they may have been disputed, but not for fraud).</li>
            </ol>
            If these conditions are satisfied, Visa may send one or more CE lookups, each targeting a specific historical transaction.
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="ce-eligibility"><span class="subtitle-1">6.3. CE Eligibility — Required Fields in OI/OID Response</span></v-card-title>
          <v-card-text>
            To be <b>eligible for CE</b>, your <b>OI/OID response</b> must always include the following:
            <b>Minimum Required Fields (always)</b>
            <ul>
              <li><code>receipt.productsPurchasedList[].productDescription</code></li>
              <li><code>merchantInformation.storeDetails.storeName</code></li>
              <li><code>merchantInformation.merchantName</code></li>
              <li><code>merchantInformation.merchantUrl</code></li>
              <li><code>merchantInformation.merchantContactPhone</code></li>
            </ul>
            <b>At least 2 of 4 persistent identifiers</b>
            <ul>
              <li><code>customerInformation.accountId</code></li>
              <li><code>deliveryAddress</code> (address1, city, region, postalCode, country)</li>
              <li><code>device.deviceId</code></li>
              <li><code>device.deviceFingerprint</code></li>
              <li><code>device.ipAddress</code></li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="ce-lookup-handling"><span class="subtitle-1">6.4. CE Lookup Handling</span></v-card-title>
          <v-card-text>
            <ul>
              <li>CE lookups are delivered to the <b>same endpoint</b> as OI/OID.</li>
              <li>The <b>payload structure is identical</b> — respond in the same way as OI/OID.</li>
              <li>Merchants are <b>not required to perform additional searches</b> or add special fields.</li>
              <li>Simply return the matching order data for the referenced historical transaction.</li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="ce-monitoring-and-validation"><span class="subtitle-1">6.5. Monitoring & Validation</span></v-card-title>
          <v-card-text>
            To stay compliant please make sure your data meets the following parameters:
            <ul>
              <li><b>IP Address:</b> must be cardholder’s public IP, IPv4/IPv6, clear text (not hashed).</li>
              <li><b>Device ID:</b> ≥15 chars, unique, not hashed.</li>
              <li><b>Device Fingerprint:</b> ≥20 chars, unique combination of device attributes (may be hashed).</li>
              <li><b>Account ID:</b> recognizable to the cardholder (e.g., username/email).</li>
              <li><b>Delivery Address:</b> full cardholder shipping address, clear text (not store address) - in case of delivery of physical goods..</li>
            </ul>
            Non-compliance may result in Visa suspending CE participation until corrected.
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="testing">7. Testing</v-card-title>
          <v-card-text>
            Merchanto provides a <b>Swagger environment</b> where you can test your Visa OI integration before going live.
            In <a href="https://dev-api.merchanto.org/api#" target="_blank">Swagger</a>, scroll down to the <b>Prevent Visa OI API</b> section — you will find two testing endpoints:
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="testing-check-response"><span class="subtitle-1">7.1 Check Response</span></v-card-title>
          <v-card-text>
            This endpoint validates the <b>format of your response payload</b>.
            <br>
            <b>How to use it:</b>
            <ol>
              <li>Click <b>Try It Out</b>.</li>
              <li>
                Paste your JSON payload into the request body textarea.
                <ul>
                  <li>Alternatively, choose one of the predefined examples to see the expected structure.</li>
                </ul>
              </li>
              <li>Click <b>Execute</b>.</li>
              <li>Review the validation result.</li>
            </ol>
          </v-card-text>
          <v-card-text>
            <b>Possible responses:</b>
            <br><br>
            <v-simple-table>
              <template #default>
                <thead>
                <tr>
                  <th class="text-left">Status Code</th>
                  <th class="text-left">Status</th>
                  <th class="text-left">Description</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td><b>200 OK</b></td>
                  <td>Valid</td>
                  <td>Your response is correctly structured and accepted.</td>
                </tr>
                <tr>
                  <td><b>400 Bad Request</b></td>
                  <td>Invalid Payload</td>
                  <td>One or more required fields are missing/invalid. Errors are listed in the response body.</td>
                </tr>
                <tr>
                  <td><b>401 Unauthorized</b></td>
                  <td>Invalid  Credentials</td>
                  <td>Username/password are wrong. (Currently not applied, planned for future use).</td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
            <br>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="testing-check-flow"><span class="subtitle-1">7.2. Check Flow</span></v-card-title>
          <v-card-text>
            This endpoint emulates the <b>full Visa OI flow</b> from lookup to response validation.
            <ul>
              <li>The request body contains a <b>predefined object</b> that simulates a Visa request.</li>
              <li>You must update its fields with details of a real transaction from your system (<code>amount</code>, <code>currency</code>, <code>authCode</code>, <code>transactionId</code>, etc.).</li>
              <li>
                When executed, the system will behave exactly as if Visa sent a live lookup:'
                <ul>
                  <li>An order is created on the Merchanto platform.</li>
                  <li>The request is forwarded to your webhook.</li>
                  <li>Your response is validated and logged.</li>
                </ul>
              </li>
            </ul>
            <b>Important requirements:</b>
            <ol>
              <li><code>clientId</code> — Must be replaced with the value provided by your Merchanto manager. This ensures the request is routed to the correct merchant integration.</li>
              <li><code>insightId</code> — Must be a <b>random UUID</b> generated for each test. Visa requests will never reuse the same <code>insightId</code>.</li>
              <li>
                <code>transactionId</code> — Must be unique per transaction.
                <ul>
                  <li>To emulate multiple lookups for the <b>same transaction</b>, keep <code>transactionId</code> unchanged.</li>
                  <li>To test different transactions, generate a new <code>transactionId</code> each time.</li>
                </ul>
              </li>
            </ol>
          </v-card-text>
          <v-card-text>
            <b>Possible responses:</b>
            <br><br>
            <v-simple-table>
              <template #default>
                <thead>
                <tr>
                  <th class="text-left">Status Code</th>
                  <th class="text-left">Status</th>
                  <th class="text-left">Description</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td><b>200 OK</b></td>
                  <td>Valid</td>
                  <td>Your system responded with valid transaction details.
                    You can log in to the Merchanto platform, go to <b>Prevent → Orders</b>, and verify the lookup details including response time.</td>
                </tr>
                <tr>
                  <td><b>400 Bad Request</b></td>
                  <td>Invalid Payload</td>
                  <td>The initial test request contained invalid Visa request details. Errors are listed in the response body.</td>
                </tr>
                <tr>
                  <td><b>401 Unauthorized</b></td>
                  <td>Invalid Credentials</td>
                  <td>Username/password are wrong. (Currently not applied, planned for future use).</td>
                </tr>
                <tr>
                  <td><b>404 Not Found</b></td>
                  <td>Transaction Not Found / Invalid Response</td>
                  <td>
                    Two possible causes:
                    <ul>
                      <li>Your system returned <b>404</b> (transaction not found).</li>
                      <li>Your system returned an invalid response.</li>
                    </ul>
                    To confirm, log in to the Merchanto platform, go to <b>Prevent → Orders</b>, find the order by <code>insightId</code>, and check the details.
                  </td>
                </tr>
                <tr>
                  <td><b>422 Unprocessable Entity</b></td>
                  <td>System Error</td>
                  <td>Most likely the <code>insightId</code> was reused instead of regenerated. Contact your manager and provide the request/response details.</td>
                </tr>
                <tr>
                  <td><b>500 Internal Server Error</b></td>
                  <td>System Error</td>
                  <td>Rare case. Contact your manager and provide the request/response details.</td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
            <br>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-text>
            <b>Best practice:</b>
            <ul>
              <li>Always regenerate <code>insightId</code> for every test.</li>
              <li>Verify all responses inside the <b>Prevent → Orders</b> section of the Merchanto platform for full transparency.</li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="performance-validation">8. Performance & Validation</v-card-title>
          <v-card-text>
            <ul>
              <li><b>Latency</b>: return responses within <b> &lt; 1000 ms</b>.</li>
              <li>
                <b>Validation</b>: Merchanto validates payload against Visa rules and our API Reference.
                <ul>
                  <li>Invalid/missing mandatory data → <b>not forwarded</b> → likely dispute escalation at the scheme.</li>
                </ul>
              </li>
              <li><b>Security</b>: HTTPS + Basic Auth are mandatory.</li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="api-reference">9. API Reference</v-card-title>
          <v-card-text>
            This section provides the full technical reference for all fields used in Visa Order Insight (OI, OID, CE) requests and responses. It is intended for developers who are building and validating the Prevent API integration. Each table describes:
            <ul>
              <li><b>Field name</b></li>
              <li><b>Type</b> (string, number, object, array, etc.)</li>
              <li><b>Constraints</b> (length, format, allowed values)</li>
              <li><b>Description</b> (business meaning and usage)</li>
            </ul>
          </v-card-text>
          <v-card-title class="subtitle-1" id="visa-oi-lookup-request-fields"><span class="subtitle-1">9.1. Visa OI Lookup Request Fields</span></v-card-title>
          <v-card-text>
            Contains the complete list of fields that Merchanto sends to the merchant when Visa initiates a lookup.
            <ul>
              <li>Includes card and transaction identifiers (<code>cardBin</code>, <code>arn</code>, <code>transactionId</code>, etc.).</li>
              <li>Explains which fields are always provided by Visa and which are conditional.</li>
              <li>Used by merchants to match the lookup to their internal transaction records.</li>
            </ul>
            <br><br>
            <v-simple-table class="payload-example-table">
              <template #default>
                <thead>
                <tr>
                  <th class="text-left">Field</th>
                  <th class="text-left">Type</th>
                  <th class="text-left">Description</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td><b>insightId*</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>true</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>36</i></p>
                  </td>
                  <td>Visa’s unique identifier for the Order Insight request to the network. Format: UUID version 4.</td>
                </tr>
                <tr>
                  <td><b>clientId*</b></td>
                  <td>
                    <p><i>integer</i></p>
                    <p><b>required:</b>&nbsp;<i>true</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>10</i></p>
                  </td>
                  <td>Unique ID for client in Visa system.</td>
                </tr>
                <tr>
                  <td><b>source*</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>true</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>3</i></p>
                  </td>
                  <td>Identifies the source of the Order Lookup Request (referred hereafter as Order Lookup Source).<br>
                    Enumerated values:<br>
                    OID = Order Insight Digital Request<br>
                    OI = Order Insight Request for Call Center Experience<br>
                    CE = Order Insight Request for Compelling Evidence
                  </td>
                </tr>
                <tr>
                  <td><b>cardBin</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>6</i></p>
                  </td>
                  <td>The six card Issuer BIN associated with the purchase. On the Merchant’s profile this field can be configured to not be provided on the request, therefore conditional, otherwise this field will always be populated.</td>
                </tr>
                <tr>
                  <td><b>cardLast4</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>4</i></p>
                  </td>
                  <td>The last four digits of the card number associated with the purchase.</td>
                </tr>
                <tr>
                  <td><b>paymentType*</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>true</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>20</i></p>
                  </td>
                  <td>Indicates the payment type of the original purchase.</td>
                </tr>
                <tr>
                  <td><b>token</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>19</i></p>
                  </td>
                  <td>Token associated with the transaction when a token was used instead of a PAN for payment. Format of the field will appear as a masked account number with the first 6 and last 4 digits available.
                  </td>
                </tr>
                <tr>
                  <td><b>paymentDescriptor*</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>true</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>25</i></p>
                  </td>
                  <td>
                    The Merchant name component of the billing descriptor which appears on the customer's transaction statement.
                  </td>
                </tr>
                <tr>
                  <td><b>paymentDescriptorContact</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>13</i></p>
                  </td>
                  <td>
                    The Merchant phone number which appears on the Cardholder’s transaction statement.
                  </td>
                </tr>
                <tr>
                  <td><b>transactionDate*</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>true</i></p>
                    <p><b>format:</b>&nbsp;<i>date-time</i></p>
                  </td>
                  <td>
                    Purchase transaction date in UTC format.
                  </td>
                </tr>
                <tr>
                  <td><b>transactionAmount*</b></td>
                  <td>
                    <p><i>object (Amount Object)</i></p>
                    <p><b>required:</b>&nbsp;<i>true</i></p>
                  </td>
                  <td>
                    The transaction amount of the payment processed by Merchant
                  </td>
                </tr>
                <tr>
                  <td><b>destinationAmount</b></td>
                  <td>
                    <p><i>object (Amount Object)</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                  </td>
                  <td>
                    Cardholder billing amount if a currency conversion took place at settlement
                  </td>
                </tr>
                <tr>
                  <td><b>arn</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                    <p><b>maxLength:</b> <i>24</i></p>
                  </td>
                  <td>Acquirer reference number.</td>
                </tr>
                <tr>
                  <td><b>authCode</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>6</i></p>
                  </td>
                  <td>The Issuer authorization code for the transaction. Available on all Visa requests.</td>
                </tr>
                <tr>
                  <td><b>terminalId</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>8</i></p>
                  </td>
                  <td>
                    Code that identifies a terminal at the card acceptor location by itself, or in conjunction with the Card Acceptor ID (when the Card Acceptor ID is not unique to a specific terminal).
                  </td>
                </tr>
                <tr>
                  <td><b>acquirerBin</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>11</i></p>
                  </td>
                  <td>
                    Acquiring Bank identifier.
                  </td>
                </tr>
                <tr>
                  <td><b>cardAcceptorId</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>15</i></p>
                  </td>
                  <td>
                    Acquirer assigned identifier of the Merchant account to VisaNet.
                  </td>
                </tr>
                <tr>
                  <td><b>mcsn</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>2</i></p>
                  </td>
                  <td>Multiple Clearing Sequence Number that contains a sequence number which distinguishes a specific clearing message.</td>
                </tr>
                <tr>
                  <td><b>purchaseIdentifier</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>25</i></p>
                  </td>
                  <td>
                    Identifier for the order, as defined by the Merchant.
                    May contain an order number, invoice number, or other reference as specified by the Merchant during payment settlement with the Acquirer.
                    Conditionally available on Visa requests, depending on how the Merchant settles payments with their Acquirer.
                  </td>
                </tr>
                <tr>
                  <td><b>transactionId</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>15</i></p>
                  </td>
                  <td>The unique transaction identifier provided by the card network.</td>
                </tr>
                <tr>
                  <td><b>transactionType</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>20</i></p>
                  </td>
                  <td>Type of transaction.</td>
                </tr>
                <tr>
                  <td><b>cardExpirationDate</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>4</i></p>
                  </td>
                  <td>
                    Card Expiration Date. Value has the format MMYY.
                  </td>
                </tr>
                <tr>
                  <td><b>mcc</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>4</i></p>
                  </td>
                  <td>Merchant category code.</td>
                </tr>
                <tr>
                  <td><b>posEntryModeCode</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>2</i></p>
                  </td>
                  <td>Identifies the terminal type and terminal entry capability.</td>
                </tr>
                <tr>
                  <td><b>eci</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>1</i></p>
                  </td>
                  <td>
                    The mail/phone/electronic commerce indicator. Possible values include space, or numbers 1 to 9
                  </td>
                </tr>
                <tr>
                  <td><b>transactionRequestId*</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>true</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>36</i></p>
                  </td>
                  <td>
                    Not a unique value, this is a hashed value based on key input fields used to locate a previous request with the same key input fields.
                    Used to optimize caching of response data by Merchant in the event the same order is requested in the future.
                    Format: UUID version 4.
                  </td>
                </tr>
                <tr>
                  <td><b>linkedInsightId</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>36</i></p>
                  </td>
                  <td>
                    Conditionally available on requests when Order Lookup source=CE <br>
                    Provides linking between multiple Compelling Evidence Order Lookup requests to the original Order Lookup request of the transaction that is under dispute.<br>
                    Format: UUID version 4.
                  </td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
            <br>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title class="subtitle-1" id="visa-oi-lookup-response-fields"><span class="subtitle-1">9.2. Visa OI Lookup Response Fields</span></v-card-title>
          <v-card-text>
            Defines all fields that a merchant can return in response to an OI/OID/CE lookup.
            <br><br>
            <v-simple-table class="payload-example-table">
              <template #default>
                <thead>
                <tr>
                  <th class="text-left">Field</th>
                  <th class="text-left">Type</th>
                  <th class="text-left">Description</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td><b>receipt*</b></td>
                  <td>
                    <p><i>object (Receipt Object)</i></p>
                    <p><b>required:</b>&nbsp;<i>true</i></p>
                  </td>
                  <td>No Description. See the Receipt Object reference.</td>
                </tr>
                <tr>
                  <td><b>merchantInformation*</b></td>
                  <td>
                    <p><i>object (MerchantInformation Object)</i></p>
                    <p><b>required:</b>&nbsp;<i>true</i></p>
                  </td>
                  <td>No Description. See the MerchantInformation Object reference.</td>
                </tr>
                <tr>
                  <td><b>customerInformation</b></td>
                  <td>
                    <p><i>object (CustomerInformation Object)</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                  </td>
                  <td>No Description. See the CustomerInformation Object reference.</td>
                </tr>
                <tr>
                  <td><b>deliveryAddress</b></td>
                  <td>
                    <p><i>object (DeliveryAddress Object)</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                  </td>
                  <td>
                    <p>This field is optional if the merchant does not sell physical goods. See the DeliveryAddress Object reference.</p>
                  </td>
                </tr>
                <tr>
                  <td><b>recipientCustomerName</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>20</i></p>
                  </td>
                  <td>
                    Recipient of goods purchased (if differs from Customer Name on customerInformation object).
                  </td>
                </tr>
                <tr>
                  <td><b>device</b></td>
                  <td>
                    <p><i>object (Device Object)</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                  </td>
                  <td>No Description. See the Device Object reference.</td>
                </tr>
                <tr>
                  <td><b>flightDetails</b></td>
                  <td>
                    <p><i>object (FlightDetails Object)</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                  </td>
                  <td>No Description. See the FlightDetails Object reference.</td>
                </tr>
                <tr>
                  <td><b>hotelDetails</b></td>
                  <td>
                    <p><i>object (HotelDetails Object)</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                  </td>
                  <td>No Description. See the HotelDetails Object reference.</td>
                </tr>
                <tr>
                  <td><b>carRentalDetails</b></td>
                  <td>
                    <p><i>object (CarRentalDetails Object)</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                  </td>
                  <td>No Description. See the CarRentalDetails Object reference.</td>
                </tr>
                <tr>
                  <td><b>rideShareDetails</b></td>
                  <td>
                    <p><i>object (RideShareDetails Object)</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                  </td>
                  <td>No Description. See the RideShareDetails Object reference.</td>
                </tr>
                <tr>
                  <td><b>communications</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>1000</i></p>
                  </td>
                  <td>
                    Any additional communications between consumer and Merchant.
                  </td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
            <br>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title class="subtitle-1" id="visa-oi-lookup-response-details-fields"><span class="subtitle-1">9.3. Detailed Object Schemas</span></v-card-title>
          <v-card-text>
            For your convenience, the full technical schemas are available as downloadable PDF files.
            You can download the complete <a :href="pdfDocs" download="VisaOIAPIReference">Visa OI Object Schema Reference (PDF)</a> directly from this page.
            <br>
            These documents include:
            <ul>
              <li>Complete field-by-field descriptions for all request and response objects.</li>
              <li>Data type definitions, format and length constraints, and required/optional indicators.</li>
              <li>Object relationships and nesting structures.</li>
              <li>Practical examples for each major object type.</li>
            </ul>
            This approach ensures you always have access to the most up-to-date, version-controlled technical specifications for Visa OI within the Prevent API.
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mdiCogOutline, mdiCheckboxBlankCircleOutline, mdiCellphoneCog, mdiChevronLeft } from '@mdi/js';
import useDocs from '@/views/docs/useDocs';
import { ref } from '@vue/composition-api';

export default {
  setup() {
    const { scrollToElement } = useDocs();

    const summary = [
      { title: '1. Endpoint Setup', link: 'endpoints' },
      { title: '2. General requirements', link: 'general-req' },
      { title: '3. Workflow', link: 'general-workflow' },
      { title: '4. Transaction Matching', link: 'transaction-matching' },
      { title: '4.1. Matching Fields', link: 'matching-fields' },
      { title: '4.2. How to implement transaction matching?', link: 'how-to-implement-matching' },
      { title: '4.3. Recommended Lookup Strategy', link: 'recommended-lookup-strategy' },
      { title: '5. Request & Response', link: 'request-response' },
      { title: '5.1. Request Example', link: 'request-example' },
      { title: '5.2. Response Example', link: 'response-example' },
      { title: '5.3. HTTP Response Codes', link: 'http-response-code' },
      { title: '5.4. Lookup Status Mapping in Merchanto Platform', link: 'lookup-status-mapping' },
      { title: '6. Compelling Evidence (CE)', link: 'compelling-evidence-ce' },
      { title: '6.1. Overview', link: 'ce-overview' },
      { title: '6.2. Conditions for CE Lookups', link: 'ce-lookups-conditions' },
      { title: '6.3. CE Eligibility — Required Fields in OI/OID Response', link: 'ce-eligibility' },
      { title: '6.4. CE Lookup Handling', link: 'ce-lookup-handling' },
      { title: '6.5. Monitoring & Validation', link: 'ce-monitoring-and-validation' },
      { title: '7. Testing', link: 'testing' },
      { title: '7.1. Check Response', link: 'testing-check-response' },
      { title: '7.2. Check Flow', link: 'testing-check-flow' },
      { title: '8. Performance & Validation', link: 'performance-validation' },
      { title: '9. API Reference', link: 'api-reference' },
      { title: '9.1. Visa OI Lookup Request Fields', link: 'visa-oi-lookup-request-fields' },
      { title: '9.2. Visa OI Lookup Response Fields', link: 'visa-oi-lookup-response-fields' },
      { title: '9.3. Detailed Object Schemas', link: 'visa-oi-lookup-response-details-fields' },
    ];

    const apiUrl = ref('https://dev-api.merchanto.org');
    const pdfDocs = ref(require('@/assets/pdf/VisaOIAPIReference.pdf').default);
    const matchingGuidance = ref(require('@/assets/images/docs/visa-oi-matching-guidance.png'));
    return {
      summary,
      apiUrl,
      pdfDocs,
      matchingGuidance,
      icons: {
        mdiCogOutline,
        mdiCheckboxBlankCircleOutline,
        mdiCellphoneCog,
        mdiChevronLeft,
      },
      scrollToElement,
    };
  },
};
</script>

<style lang="scss">
@import '@core/preset/preset/pages/knowledge-base';
</style>
